import BaseView from './BaseView';
let view = null;

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

import debounce from 'lodash.debounce';
import Footer from '../../components/Footer';

class TemplateWork extends BaseView {
	constructor(container) {
		super(container);
    }

	init() {
        const groups = new Map([
			[Footer, this.container.querySelectorAll('[data-footer]')],
		]);
        super.createModules(groups);

        this.lockBody();

        this.workItemsWrapper = this.container.querySelector('[data-work-items]')
        this.workItems = this.container.querySelectorAll('[data-work-item]');

        this.workImageWrapper = this.container.querySelector('[data-work-images]');
        this.workImages = this.container.querySelectorAll('[data-work-image]');

        const mm = gsap.matchMedia();
        this.mm = mm.add({
            	isMobile: `(max-width: 991px)`,
            	isDesktop: `(min-width: 992px)`,
            	reduceMotion: "(prefers-reduced-motion: reduce)"
        	}, (context) => {

            let { isMobile, isDesktop, reduceMotion } = context.conditions;

            this.isMobile = isMobile;
            this.isDesktop = isDesktop;
            this.reduceMotion = reduceMotion;
		});

        this.createTimeline();

		this.workItems.forEach((elm, index) => {

            ScrollTrigger.create({
                scroller: this.container,
                trigger: elm,
                start: 'top '+window.innerHeight/2,
                end: 'bottom '+window.innerHeight/2,
                onEnter: () => {
                    this.activateItem(elm, index);
                },
                onEnterBack: () => {
                    this.activateItem(elm, index);
                },
                onLeave: () => {
                    this.deactivateItem(elm);
                },
                onLeaveBack: () => {
                    this.deactivateItem(elm);
                }
            });

            // Scroll to focused element, but dont cancel click events by doing so..
            elm.querySelector('a').addEventListener('mousedown', e => {
                elm.setAttribute('data-clicked', true);
            });
            elm.querySelector('a').addEventListener('mouseup', e => {
                elm.removeAttribute('data-clicked', false);
            });
            elm.querySelector('a').addEventListener('focus', e => {
                if(elm.getAttribute('data-clicked')) {
                    e.preventDefault();
                    elm.removeAttribute('data-clicked', false);
                    return;
                }
                gsap.to(this.container, {
                    scrollTo: {
                        y: elm,
                        offsetY: (window.innerHeight/2)-(elm.getBoundingClientRect().height/2)
                    },
                    duration: 0.3
                });
            });
        });

        this.boundResizeListener = debounce(() => {
            this.onResize();
        }, 200);
		window.addEventListener(
			'resize',
			this.boundResizeListener
		);

	}

    onResize() {
        setTimeout(() => {
            this.createTimeline();
            const triggers = ScrollTrigger.getAll();
            triggers.forEach(trigger => {
                trigger.update();
                trigger.refresh();
            });
        }, 0);
    }

    lockBody() {
        document.dispatchEvent(new CustomEvent('disable-scroll', {
            detail: {
                scrollingElm: this.container,
            }
        }));

        this.container.classList.add('ready-to-scroll');
    }

    lockContainer() {
        this.container.classList.remove('ready-to-scroll');
    }

    activateItem(elm, index) {
        elm.classList.add('is-active');

        this.workImages.forEach(elm => {
            elm.classList.remove('is-visible');
        })
        this.workImages[this.workImages.length-1-index].classList.add('is-visible');

        if(index===(this.workItems.length-1)) {
            document.body.classList.add('show-footer');
            document.dispatchEvent(new CustomEvent('hide-switcher'));
        } else {
            document.body.classList.remove('show-footer');
            document.dispatchEvent(new CustomEvent('show-switcher'));
        }
    }

    deactivateItem(elm) {
        elm.classList.remove('is-active');
    }

    createTimeline() {
        if(this.imageTimeline) {
            this.imageTimeline.scrollTrigger.kill();
            this.imageTimeline.killTweensOf();
            this.imageTimeline.kill();
            this.imageTimeline = null;
            this.workImageWrapper.removeAttribute('style');
        }
        if(this.isMobile) {
            this.workImages.forEach(image => {
                image.removeAttribute('style');
            });
            return;
        }
        if(this.reduceMotion) {
            return;
        }

        const itemHeight = this.workItems[0].getBoundingClientRect().height;

        this.imageTimeline = gsap.timeline({
            paused: true,
            scrollTrigger: {
                scroller: this.container,
                trigger: this.workItemsWrapper,
                scrub: 0.3,
                start: 'top '+(window.innerHeight/2)+'-='+(itemHeight/2),
                end: 'bottom '+(window.innerHeight/2)+'+='+(itemHeight/2),
                markers:  false
            }
        });

        let totalOffset = 0;
        let prevImageHeight = this.workImages[this.workImages.length-1].getBoundingClientRect().height;
        const lastHeight = this.workImages[0].getBoundingClientRect().height;
        const animationHeightSpan = this.workImageWrapper.getBoundingClientRect().height-(prevImageHeight/2)-(lastHeight/2);
        gsap.set(this.workImageWrapper, {
            marginBottom: -1 * (prevImageHeight/2),
        });


        for (let loopIndex = this.workImages.length - 1; loopIndex >= 0; loopIndex--) {
            const index = (this.workImages.length-loopIndex)-1;
            const image = this.workImages[loopIndex];

            if(index>0) {
                const itemOffset = (prevImageHeight/2)+(image.getBoundingClientRect().height/2);
                totalOffset = totalOffset+itemOffset;
                this.imageTimeline.addLabel('label-'+index);

                this.imageTimeline.to(this.workImages[loopIndex+1], {
                    opacity: 0,
                    ease: 'power1.inOut',
                    duration: (animationHeightSpan*this.workImages.length)-itemOffset
                }, 'label-'+index);

                this.imageTimeline.to(this.workImages[loopIndex], {
                    opacity: 1,
                    ease: 'power1.inOut',
                    duration: (animationHeightSpan*this.workImages.length)-itemOffset
                }, 'label-'+index);

                this.imageTimeline.to(this.workImageWrapper, {
                    y: totalOffset,
                    ease: 'none',
                    duration: (animationHeightSpan*this.workImages.length)-itemOffset
                }, 'label-'+index);
            }
            prevImageHeight = image.getBoundingClientRect().height;
        };
    }

	destroy() {
		document.body.classList.remove('show-footer');
        this.container.classList.remove('ready-to-scroll');
	}
}

export default {
	namespace: 'template-work',
	beforeEnter: ({ current, next, trigger }) => {
		view = new TemplateWork(next.container);
		view.init();
	},
	afterLeave: ({ current, next, trigger }) => {
		view.destroy();
		view = null;
	}
};
